import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { portalService } from '../services/portalService';
import { usePortal } from '../contexts/PortalContext';
import AssetCard from './AssetCard';
import FiltersRail from './FiltersRail';
import LoadingSpinner from './LoadingSpinner';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSearch, FiFilter } = FiIcons;

const AssetCatalog = () => {
  const { id: companyId } = useParams();
  const { companies, categories, logAssetClick } = usePortal();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const company = companies.find(c => c.id === companyId);

  useEffect(() => {
    fetchAssets();
  }, [companyId, filters]);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await portalService.getCompanyAssets(companyId, filters);
      setAssets(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAssetClick = async (asset) => {
    await logAssetClick(asset.id, companyId);
    window.open(asset.url, '_blank');
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  if (!company) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900">Company not found</h3>
          <p className="mt-1 text-sm text-gray-500">The requested company could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Company header */}
      <div className="mb-8">
        <div className="flex items-center space-x-4">
          {company.logo && (
            <img
              src={company.logo}
              alt={`${company.name} logo`}
              className="h-16 w-16 rounded-lg object-cover"
            />
          )}
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{company.name}</h1>
            <p className="text-gray-600 mt-1">{company.description}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Filters sidebar */}
        <div className={`lg:w-64 ${showFilters ? 'block' : 'hidden lg:block'}`}>
          <FiltersRail
            categories={categories}
            filters={filters}
            onFilterChange={handleFilterChange}
          />
        </div>

        {/* Main content */}
        <div className="flex-1">
          {/* Search and filters header */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Search assets..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange({ search: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <SafeIcon icon={FiFilter} className="h-5 w-5" />
                <span>Filters</span>
              </button>
            </div>
          </div>

          {/* Assets grid */}
          {loading ? (
            <div className="grid-responsive">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded mb-4 w-3/4"></div>
                    <div className="flex justify-between items-center mb-4">
                      <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                      <div className="h-6 w-6 bg-gray-200 rounded"></div>
                    </div>
                    <div className="flex space-x-2">
                      <div className="h-6 bg-gray-200 rounded w-16"></div>
                      <div className="h-6 bg-gray-200 rounded w-20"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900">Error loading assets</h3>
              <p className="mt-1 text-sm text-gray-500">{error}</p>
              <button
                onClick={fetchAssets}
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Try again
              </button>
            </div>
          ) : assets.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900">No assets found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {filters.search || filters.category
                  ? 'Try adjusting your filters to see more results.'
                  : 'No assets are available for this company.'}
              </p>
            </div>
          ) : (
            <div className="grid-responsive">
              {assets.map((asset, index) => (
                <motion.div
                  key={asset.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <AssetCard
                    asset={asset}
                    category={categories.find(c => c.id === asset.categoryId)}
                    onClick={() => handleAssetClick(asset)}
                  />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetCatalog;